(() => {
  const header = document.querySelector("header.navimow-header");
  const nav = header?.querySelector(".navimow-header_nav");
  const mobileMenuInner = nav?.querySelector(".navimow-header_mobile-menu-inner");
  const mobileMenuButton = header?.querySelector(
    ".navimow-header_mobile-menu-button",
  );
  if (!header || !nav || !mobileMenuInner || !mobileMenuButton) return;

  const mediaList = window.matchMedia("(min-width: 1024px)");
  const MOBILE_MENU_TRANSITION_MS = 240;
  const addMediaChangeListener = (query, callback) => {
    if (typeof query.addEventListener === "function") {
      query.addEventListener("change", callback);
      return;
    }
    query.addListener(callback);
  };

  const isMobileMenuOpen = () =>
    mobileMenuButton.getAttribute("aria-expanded") === "true";

  const updateNavAccessibility = () => {
    const isInteractive = mediaList.matches || isMobileMenuOpen();
    nav.setAttribute("aria-hidden", isInteractive ? "false" : "true");
  };

  let headerTop = 0;
  let headerTopFrame = 0;
  let scrollLockFrame = 0;
  let scrollLockFrameAfterPaint = 0;
  let closeCleanupTimeout = 0;
  let closeCleanupController = null;
  const syncMobileMenuOffset = () => {
    const headerContainer = header.parentElement;
    const top = headerContainer
      ? Math.max(0, Math.round(headerContainer.getBoundingClientRect().top))
      : 0;
    if (top === headerTop) return;

    headerTop = top;
    document.documentElement.style.setProperty("--header-top", `${top}px`);
  };

  const scheduleMobileMenuOffsetSync = () => {
    if (headerTopFrame) return;

    headerTopFrame = requestAnimationFrame(() => {
      headerTopFrame = 0;
      syncMobileMenuOffset();
    });
  };

  const clearScheduledScrollLock = () => {
    if (scrollLockFrame) {
      cancelAnimationFrame(scrollLockFrame);
      scrollLockFrame = 0;
    }

    if (scrollLockFrameAfterPaint) {
      cancelAnimationFrame(scrollLockFrameAfterPaint);
      scrollLockFrameAfterPaint = 0;
    }
  };

  const scheduleBodyScrollLock = () => {
    clearScheduledScrollLock();

    // Let the menu paint first, then lock body scroll outside the INP window.
    scrollLockFrame = requestAnimationFrame(() => {
      scrollLockFrame = 0;
      scrollLockFrameAfterPaint = requestAnimationFrame(() => {
        scrollLockFrameAfterPaint = 0;

        if (!isMobileMenuOpen()) return;
        document.body.classList.add("no-scroll");
      });
    });
  };

  const clearScheduledCloseCleanup = () => {
    if (closeCleanupTimeout) {
      clearTimeout(closeCleanupTimeout);
      closeCleanupTimeout = 0;
    }

    closeCleanupController?.abort();
    closeCleanupController = null;
  };

  const runCloseCleanup = () => {
    clearScheduledCloseCleanup();
    document.body.classList.remove("no-scroll");
    resetExpandableMenus();
  };

  const resetExpandableMenus = () => {
    nav
      .querySelectorAll(".header-menu-list, .header-menu-mega, .header-menu-multilist")
      .forEach((detail) => {
        detail.classList.remove("header-menu-open");
        detail.classList.add("header-menu-closed");
      });

    nav.querySelectorAll("button[aria-expanded]").forEach((button) => {
      button.setAttribute("aria-expanded", "false");
    });

    nav.querySelectorAll(".header-menu-mega").forEach((detail) => {
      detail.style.setProperty("--header-menu-mega-content-height", "0px");
    });

    nav.querySelectorAll(".header-menu-mega-item").forEach((item) => {
      item.style.removeProperty("--mega-item-content-height");
    });
  };

  const scheduleMobileMenuCloseCleanup = (deferCloseCleanup) => {
    clearScheduledCloseCleanup();

    if (!deferCloseCleanup) {
      runCloseCleanup();
      return;
    }

    closeCleanupController = new AbortController();
    const { signal } = closeCleanupController;
    const completeClose = (event) => {
      if (
        event &&
        (event.target !== mobileMenuInner || event.propertyName !== "opacity")
      ) {
        return;
      }

      runCloseCleanup();
    };

    mobileMenuInner.addEventListener("transitionend", completeClose, {
      signal,
    });
    closeCleanupTimeout = window.setTimeout(
      completeClose,
      MOBILE_MENU_TRANSITION_MS + 80,
    );
  };

  const setMobileMenuOpen = (
    open,
    { deferCloseCleanup = !mediaList.matches } = {},
  ) => {
    header.setAttribute("data-mobile-menu-open", String(open));
    mobileMenuButton.setAttribute("aria-expanded", String(open));

    if (open) {
      clearScheduledCloseCleanup();
      scheduleBodyScrollLock();
    } else {
      clearScheduledScrollLock();
      scheduleMobileMenuCloseCleanup(deferCloseCleanup);
    }

    updateNavAccessibility();
  };

  nav.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;

    if (!mediaList.matches) {
      setMobileMenuOpen(false);
      mobileMenuButton.focus();
      return;
    }

    resetExpandableMenus();
    updateNavAccessibility();
  });

  mobileMenuButton.addEventListener("click", () => {
    scheduleMobileMenuOffsetSync();
    setMobileMenuOpen(!isMobileMenuOpen());
  });

  addMediaChangeListener(mediaList, () => {
    scheduleMobileMenuOffsetSync();
    setMobileMenuOpen(false, { deferCloseCleanup: false });
  });

  window.addEventListener(
    "resize",
    scheduleMobileMenuOffsetSync,
    { passive: true },
  );

  scheduleMobileMenuOffsetSync();
  updateNavAccessibility();

  const bindResponsiveMode = (bind) => {
    let cleanup = () => {};

    const apply = () => {
      cleanup();
      cleanup = bind(mediaList.matches) || (() => {});
    };

    apply();
    addMediaChangeListener(mediaList, apply);
  };

  /**
   * list menu
   */
  const listDetails = nav.querySelectorAll(".header-menu-list");

  listDetails.forEach((detail) => {
    const button = detail.querySelector(".header-menu-list-button");
    const detailContent = detail.querySelector(
      ".header-menu-list-content_list",
    );
    if (!button || !detailContent?.firstElementChild) return;

    const measureContentHeight = () => {
      detail.style.setProperty(
        "--header-menu-list-content-height",
        `${detailContent.firstElementChild.scrollHeight}px`,
      );
    };

    measureContentHeight();

    const transitionReset = () => {
      detail.classList.add("header-menu-closed");
      button.setAttribute("aria-expanded", "false");
    };

    detailContent.addEventListener("transitionend", (event) => {
      if (event.target !== detailContent) return;
      if (detail.classList.contains("header-menu-open")) return;
      transitionReset();
    });

    let mouseOverTimeout = null;

    const triggerHeightTransition = (open) => {
      if (open) {
        measureContentHeight();
        detail.classList.add("header-menu-open");
        detail.classList.remove("header-menu-closed");
        button.setAttribute("aria-expanded", "true");
      } else {
        detail.classList.remove("header-menu-open");
        if (detailContent.clientHeight === 0) {
          transitionReset();
        }
      }
    };

    const mouseEnterHandler = () => {
      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = setTimeout(() => {
        triggerHeightTransition(true);
      }, 120);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const mouseLeaveHandler = () => {
      clearTimeout(mouseOverTimeout);
      triggerHeightTransition(false);
    };

    bindResponsiveMode((isDesktop) => {
      clearTimeout(mouseOverTimeout);
      const controller = new AbortController();
      const { signal } = controller;

      if (isDesktop) {
        detail.addEventListener("mouseenter", mouseEnterHandler, { signal });
        button.addEventListener("focus", mouseEnterHandler, { signal });
      } else {
        button.addEventListener(
          "click",
          () => {
            triggerHeightTransition(!detail.classList.contains("header-menu-open"));
          },
          { signal },
        );
      }

      return () => {
        clearTimeout(mouseOverTimeout);
        detail.removeEventListener("mouseleave", mouseLeaveHandler);
        controller.abort();
      };
    });
  });

  /**
   * mega menu
   */

  const megaDetails = nav.querySelectorAll(".header-menu-mega");
  const MEGA_MENU_OPEN_DELAY = 140;
  const MEGA_MENU_SWITCH_DELAY = 260;
  const MEGA_MENU_CLOSE_DELAY = 160;
  const MEGA_ITEM_HOVER_DELAY = 180;
  const MEGA_ITEM_INTENT_DELAY = 320;
  const MOUSE_LOCATIONS_TRACKED = 3;

  megaDetails.forEach((detail) => {
    const button = detail.querySelector(".header-menu-mega-button");
    const detailContent = detail.querySelector(
      ".header-menu-mega-content_list",
    );
    if (!button || !detailContent?.firstElementChild) return;

    detail.style.setProperty("--header-menu-mega-content-height", `${0}px`);

    const transitionReset = () => {
      detail.classList.add("header-menu-closed");
      button.setAttribute("aria-expanded", "false");
    };

    detailContent.addEventListener("transitionend", (event) => {
      if (event.target !== detailContent) return;
      if (detail.classList.contains("header-menu-open")) return;
      transitionReset();
    });

    let mouseOverTimeout = null;
    let mouseLeaveTimeout = null;
    let megaItemHoverTimeout = null;
    let activeMegaItemIdx = 0;
    const mouseLocations = [];

    const triggerHeightTransition = (open) => {
      if (open) {
        detail.classList.add("header-menu-open");
        detail.classList.remove("header-menu-closed");
        button.setAttribute("aria-expanded", "true");
      } else {
        detail.classList.remove("header-menu-open");
        if (detailContent.clientHeight === 0) {
          transitionReset();
        }
      }
    };

    const clearMegaItemHoverTimeout = () => {
      clearTimeout(megaItemHoverTimeout);
      megaItemHoverTimeout = null;
    };

    const resetMouseLocations = () => {
      mouseLocations.length = 0;
    };

    const trackMouseLocation = (event) => {
      mouseLocations.push({ x: event.clientX, y: event.clientY });
      if (mouseLocations.length > MOUSE_LOCATIONS_TRACKED) {
        mouseLocations.shift();
      }
    };

    const getOtherOpenedMega = () =>
      Array.from(megaDetails).find(
        (item) =>
          item !== detail && item.classList.contains("header-menu-open"),
      );

    const mouseEnterHandler = () => {
      clearTimeout(mouseLeaveTimeout);
      clearTimeout(mouseOverTimeout);
      const delay = getOtherOpenedMega()
        ? MEGA_MENU_SWITCH_DELAY
        : MEGA_MENU_OPEN_DELAY;
      mouseOverTimeout = setTimeout(() => {
        triggerHeightTransition(true);
        mouseOverTimeout = null;
      }, delay);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const mouseLeaveHandler = () => {
      clearTimeout(mouseOverTimeout);
      clearMegaItemHoverTimeout();
      clearTimeout(mouseLeaveTimeout);
      mouseLeaveTimeout = setTimeout(() => {
        triggerHeightTransition(false);
        resetMouseLocations();
        mouseLeaveTimeout = null;
      }, MEGA_MENU_CLOSE_DELAY);
    };

    const megaItemButton = Array.from(
      detail.querySelectorAll(".header-menu-mega-item-button"),
    );
    const resetMegaItemState = () => {
      megaItemButton.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
        item.parentElement?.style.removeProperty("--mega-item-content-height");
      });
    };

    megaItemButton.forEach((item, index) => {
      item.dataset.idx = index;
    });

    const openMegaItemDetail = (idx) => {
      const currentButton = megaItemButton[idx];
      if (!currentButton) return;
      activeMegaItemIdx = idx;
      megaItemButton.forEach((item) => {
        item.setAttribute("aria-expanded", "false");
      });
      currentButton.setAttribute("aria-expanded", "true");

      const currentContent = currentButton.nextElementSibling;
      detail.style.setProperty(
        "--mega-item-content-height",
        `${currentContent.scrollHeight}px`,
      );
      detail.style.setProperty(
        "--header-menu-mega-content-height",
        `${detailContent.firstElementChild.clientHeight}px`,
      );

      // 防止 swiper 的 bug
      const swiperNodes =
        currentButton.parentElement.querySelectorAll(".swiper");
      swiperNodes.forEach((node) => {
        const swiper = node.swiper;
        if (swiper) swiper.scrollbar.enable();
      });
    };

    const getMegaItemHoverDelay = (nextIdx) => {
      if (nextIdx === activeMegaItemIdx) return 0;

      const currentButton = megaItemButton[activeMegaItemIdx];
      const currentContent = currentButton?.nextElementSibling;
      const firstLocation = mouseLocations[0];
      const latestLocation = mouseLocations[mouseLocations.length - 1];

      if (!currentContent || !firstLocation || !latestLocation) {
        return MEGA_ITEM_HOVER_DELAY;
      }

      const currentContentRect = currentContent.getBoundingClientRect();
      const isMovingTowardContent =
        latestLocation.x > firstLocation.x &&
        latestLocation.x <= currentContentRect.left + 48 &&
        latestLocation.y >= currentContentRect.top - 80 &&
        latestLocation.y <= currentContentRect.bottom + 80 &&
        firstLocation.x < currentContentRect.left &&
        firstLocation.y >= currentContentRect.top - 120 &&
        firstLocation.y <= currentContentRect.bottom + 120;

      return isMovingTowardContent
        ? MEGA_ITEM_INTENT_DELAY
        : MEGA_ITEM_HOVER_DELAY;
    };

    const scheduleMegaItemDetail = (idx) => {
      clearMegaItemHoverTimeout();

      const delay = getMegaItemHoverDelay(idx);
      if (delay === 0) return;

      megaItemHoverTimeout = setTimeout(() => {
        openMegaItemDetail(idx);
        megaItemHoverTimeout = null;
      }, delay);
    };

    bindResponsiveMode((isDesktop) => {
      clearTimeout(mouseOverTimeout);
      clearTimeout(mouseLeaveTimeout);
      clearMegaItemHoverTimeout();
      resetMouseLocations();

      const controller = new AbortController();
      const { signal } = controller;

      if (isDesktop) {
        detail.addEventListener("mousemove", trackMouseLocation, { signal });
        detail.addEventListener("mouseenter", mouseEnterHandler, { signal });
        button.addEventListener(
          "focus",
          () => {
            clearTimeout(mouseLeaveTimeout);
            triggerHeightTransition(true);
          },
          { signal },
        );

        megaItemButton.forEach((item) => {
          item.addEventListener(
            "mouseenter",
            () => {
              const idx = Number(item.dataset.idx);
              scheduleMegaItemDetail(idx);
            },
            { signal },
          );
          item.addEventListener(
            "mouseleave",
            () => {
              clearMegaItemHoverTimeout();
            },
            { signal },
          );
          item.addEventListener(
            "focus",
            () => {
              clearMegaItemHoverTimeout();
              const idx = Number(item.dataset.idx);
              openMegaItemDetail(idx);
            },
            { signal },
          );
        });

        openMegaItemDetail(0);
      } else {
        button.addEventListener(
          "click",
          () => {
            const willOpen = !detail.classList.contains("header-menu-open");

            detail.style.setProperty(
              "--header-menu-mega-content-height",
              `${detailContent.firstElementChild.clientHeight}px`,
            );

            if (!willOpen) {
              resetMegaItemState();
            }

            triggerHeightTransition(willOpen);
          },
          { signal },
        );

        megaItemButton.forEach((item) => {
          item.addEventListener(
            "click",
            () => {
              const isExpanded = item.getAttribute("aria-expanded") === "true";
              const currentContent = item.nextElementSibling?.querySelector(
                ".mega-item-content_inner",
              );
              if (!currentContent) return;

              item.parentElement?.style.setProperty(
                "--mega-item-content-height",
                `${currentContent.scrollHeight}px`,
              );
              item.setAttribute("aria-expanded", isExpanded ? "false" : "true");

              if (!isExpanded) {
                detail.style.setProperty(
                  "--header-menu-mega-content-height",
                  "auto",
                );
              }
            },
            { signal },
          );

          item.nextElementSibling?.addEventListener(
            "transitionend",
            (event) => {
              if (event.target !== item.nextElementSibling) return;
              const height = detailContent.firstElementChild.clientHeight;
              detail.style.setProperty(
                "--header-menu-mega-content-height",
                `${height}px`,
              );
            },
            { signal },
          );
        });
      }

      return () => {
        clearTimeout(mouseOverTimeout);
        clearTimeout(mouseLeaveTimeout);
        clearMegaItemHoverTimeout();
        resetMouseLocations();
        detail.removeEventListener("mouseleave", mouseLeaveHandler);
        controller.abort();
      };
    });
  });

  /**
   * multilist menu
   */

  const multilistDetails = nav.querySelectorAll(".header-menu-multilist");

  multilistDetails.forEach((detail) => {
    const button = detail.querySelector(".header-menu-multilist-button");
    const detailContent = detail.querySelector(
      ".header-menu-multilist-content_list",
    );
    if (!button || !detailContent?.firstElementChild) return;

    const measureContentHeight = () => {
      detail.style.setProperty(
        "--header-menu-multilist-content-height",
        `${detailContent.firstElementChild.scrollHeight}px`,
      );
    };

    measureContentHeight();

    const transitionReset = () => {
      detail.classList.add("header-menu-closed");
      button.setAttribute("aria-expanded", "false");
    };

    detailContent.addEventListener("transitionend", (event) => {
      if (event.target !== detailContent) return;
      if (detail.classList.contains("header-menu-open")) return;
      transitionReset();
    });

    let mouseOverTimeout = null;

    const triggerHeightTransition = (open) => {
      if (open) {
        measureContentHeight();
        detail.classList.add("header-menu-open");
        detail.classList.remove("header-menu-closed");
        button.setAttribute("aria-expanded", "true");
      } else {
        detail.classList.remove("header-menu-open");
        if (detailContent.clientHeight === 0) {
          transitionReset();
        }
      }
    };

    const mouseEnterHandler = () => {
      clearTimeout(mouseOverTimeout);
      mouseOverTimeout = setTimeout(() => {
        triggerHeightTransition(true);
      }, 120);
      detail.addEventListener("mouseleave", mouseLeaveHandler, { once: true });
    };

    const mouseLeaveHandler = () => {
      clearTimeout(mouseOverTimeout);
      triggerHeightTransition(false);
    };

    bindResponsiveMode((isDesktop) => {
      clearTimeout(mouseOverTimeout);
      const controller = new AbortController();
      const { signal } = controller;

      if (isDesktop) {
        detail.addEventListener("mouseenter", mouseEnterHandler, { signal });
        button.addEventListener("focus", mouseEnterHandler, { signal });
      } else {
        button.addEventListener(
          "click",
          () => {
            triggerHeightTransition(!detail.classList.contains("header-menu-open"));
          },
          { signal },
        );
      }

      return () => {
        clearTimeout(mouseOverTimeout);
        detail.removeEventListener("mouseleave", mouseLeaveHandler);
        controller.abort();
      };
    });
  });

  // track event

  const trackEvent = (data) => {
    window.dataLayer = window.dataLayer || [];
    const current_page = window.location.href;
    window.dataLayer.push({
      event: "ga4Event",
      event_name: "header-cta",
      event_parameters: {
        current_page: current_page,
        ...data,
      },
    });
  };

  const headerMenuMegaLink = header.querySelectorAll(".header-menu-mega-link-item");
  headerMenuMegaLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-mega")
          .querySelector(".header-menu-mega-button").innerText,
        button_name: link.querySelector(".header-menu-mega-link-text")
          ?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuMegaItemBannerLink = header.querySelectorAll(
    ".header-menu-mega-item-banner-link",
  );
  headerMenuMegaItemBannerLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-mega")
          ?.querySelector(".header-menu-mega-button")?.innerText,
        subheading: link
          .closest(".header-menu-mega-item")
          ?.querySelector(".header-menu-mega-item-button-text")?.innerText,
        title: link?.querySelector(".header-menu-mega-item-banner-link-title")
          ?.innerText,
        button_name: "banner_image",
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuMegaItemViewAllLink = header.querySelectorAll(
    ".header-menu-mega-item-view-all-link",
  );
  headerMenuMegaItemViewAllLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-mega")
          ?.querySelector(".header-menu-mega-button")?.innerText,
        subheading: link
          .closest(".header-menu-mega-item")
          ?.querySelector(".header-menu-mega-item-button-text")?.innerText,
        button_name: link.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuMegaItemProductLink = header.querySelectorAll(
    ".header-menu-mega-item-product-link",
  );
  headerMenuMegaItemProductLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-mega")
          ?.querySelector(".header-menu-mega-button")?.innerText,
        subheading: link
          .closest(".header-menu-mega-item")
          ?.querySelector(".header-menu-mega-item-button-text")?.innerText,
        title: link?.querySelector(".header-menu-mega-item-product-title")
          ?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuListItemLink = header.querySelectorAll(
    ".header-menu-list-item-link",
  );
  headerMenuListItemLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-multilist")
          ?.querySelector(".header-menu-multilist-button")?.innerText,
        subheading: link
          .closest(".header-menu-list-item")
          ?.querySelector(".header-menu-list-item-title")?.innerText,
        button_name: link?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuListLink = header.querySelectorAll(".header-menu-list-link");
  headerMenuListLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: link
          .closest(".header-menu-list")
          ?.querySelector(".header-menu-list-button")?.innerText,
        button_name: link?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

  const headerMenuLink = header.querySelectorAll(".header-menu-link");
  headerMenuLink.forEach((link) => {
    link.addEventListener("click", (event) => {
      trackEvent({
        heading: "header menu link",
        button_name: link?.innerText,
        link: link.getAttribute("href"),
      });
    });
  });

})();
